import { computed, effect, Inject, Injectable, PLATFORM_ID, signal, untracked } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { Product, Product_SkuColor } from '@proto/store/api/store/product';
import { Skus } from '@helper/skus';
import { MarketingCategoryModel } from '@common/marketing-category/marketing-category.model';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { Constants } from '@helper/constants';
import { ProductRepositoryService } from '@service/product-repository.service';
import { CartModel } from '../cart/cart.model';
import { BaseProductModel } from './base-product.model';
import { MetaData } from '@helper/meta-data';
import { ConfirmData, ConfirmDialogComponent } from '@common/confirm-dialog/confirm-dialog.component';

import { isEqual } from 'lodash-es';

@Injectable()
export class ProductModel extends BaseProductModel {
  readonly colorId = computed(() => {
    return this.hoverColorId() || (this.currColor()?.id ?? '');
  });
  readonly hoverColorId = signal('');
  readonly hoverProductImg = computed(() => {
    const color = this.apiResult().colors.find(c => c.id === this.hoverColorId());
    return Skus.getImage500(color, this.currSize()?.size);
  });
  readonly outOfStock = computed(() => {
    const inv =
      this.apiResult().inventory[
        Skus.skuId(this.apiResult().meta?.productId ?? '', this.colorId(), this._selectSize()?.size.id ?? '')
      ];
    return inv && !inv.num && inv.stockRemind;
  });
  readonly shortColors = computed(() => this.apiResult().colors.slice(0, Constants.MOBILE_DISPLAY_COLOR_COUNT), {
    equal: (a: Product_SkuColor[], b: Product_SkuColor[]) => isEqual(a, b),
  });
  readonly updated = signal(false);

  constructor(
    dg: MatDialog,
    repository: ProductRepositoryService,
    private mCL: MarketingCategoryModel,
    cart: CartModel,
    private ln: Location,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    super(dg, repository, cart);
    effect(() => {
      const colors = this.apiResult().colors;
      if (!colors.some(c => c.id === untracked(this._selectColor)?.id)) {
        this._selectColor.set(colors[0]);
        this._selectSize.set(this.availableSizes()[0]);
        this.setCount(1);
        return;
      }
      let sizeName = untracked(this._selectSize)?.size.name;
      if (!sizeName) {
        sizeName = this.lastSize();
      } else {
        this.lastSize.set(sizeName);
      }
      const size = this.availableSizes().find(s => s.size.name === sizeName) ?? undefined;
      if (!this.hoverColorId() && this._selectSize()) {
        const currMaxNum = size?.inventory?.num ?? 0;
        if (this.count() > currMaxNum && currMaxNum) {
          this.setCount(currMaxNum);
        }
      }
      this._selectSize.set(size);
    });
    effect(() => {
      const product = this.apiResult();
      if (product.meta?.productId && !product.published && isPlatformBrowser(this.platformId)) {
        ConfirmDialogComponent.open(
          dg,
          new ConfirmData({
            content: $localize`商品已下架`,
            singleBtnTitle: $localize`確定`,
          })
        )
          .afterClosed()
          .subscribe(() => this.router.navigate([RouteHelper.home()], { replaceUrl: true }));
      }
    });
  }

  productUpdated(sku: Skus): void {
    const color = this.apiResult().colors.find(c => c.id === sku.colorId) ?? this.apiResult().colors[0];
    this._selectColor.set(color);
    const size = this.availableSizes().find(s => s.size.id === sku.sizeId) ?? this.availableSizes()[0];
    this._selectSize.set(size);
    if (this.ln.path().includes(RoutePaths.PRODUCT)) {
      this.mCL.currentBigId.set(this.apiResult().meta?.big?.nameEn ?? '');
      this.mCL.currentMediumId.set(this.apiResult().medium?.nameEn ?? '');
      this.mCL.currentSmallId.set(this.apiResult().small?.nameEn ?? '');
    }
    if (isPlatformBrowser(this.platformId) && !this.updated()) {
      setTimeout(() => this.updated.set(true), 0);
    }
  }

  onHover(colorId: string): void {
    this.hoverColorId.set(colorId);
  }

  override getMetaByUrl(segments: string[], model: Product): MetaData | undefined {
    if (segments[0] !== RoutePaths.PRODUCT) {
      return undefined;
    }
    return new MetaData(model.meta?.name ?? '', $localize`發現一個不錯的商品，趕快來看看吧!`, model.shareImage, true);
  }
}
